import { API } from './types'
import { useQueryClient } from 'react-query'

const useOptimisticUpdate = () => {
  const client = useQueryClient()

  /**
   * DO NOT MODIFY THIS FUNCTION UNLESS YOU ARE 100% SURE ABOUT:
   *
   * - what you are doing
   * - how react-query works
   *
   * This function should be as generic as possible, so please, PLEASE, do not
   * add an IF or any other weird validation or I will personally go to your
   * house and hit you with a stick.
   */
  const update = <State, Value>(params: API.OptimisticParams<State, Value>) => {
    const {
      cacheKey = '',
      invalidate,
      cacheUpdater,
      cacheUpdateMode = 'optimistic',
      callback
    } = params

    const updateCache = (snapshot, mutatingValue) => {
      const paginatedSnapshot = !snapshot?.data

      // cache update for regular queries
      if (!paginatedSnapshot) {
        client.setQueryData(cacheKey, {
          ...snapshot,
          data: cacheUpdater(snapshot.data, mutatingValue)
        })
      }

      // cache update for paginated queries
      if (!!paginatedSnapshot) {
        const pages = snapshot?.pages || []

        const pageIndex = pages?.findIndex((page) => {
          if (Array.isArray(page?.data)) {
            return !!page?.data?.filter?.((item) => {
              return item?.id === mutatingValue?.id
            })?.length
          }

          return -1
        })

        const targetPage = pageIndex === -1 ? 0 : pageIndex

        /**
         * Page found:
         * - modify the page by it's index
         *
         * Page not found:
         * - Probably is new data being added, so we modify the first page
         */
        pages[targetPage] = {
          data: cacheUpdater(pages?.[targetPage]?.data, mutatingValue)
        }

        client.setQueryData(cacheKey, { ...snapshot, pages })
      }
    }

    return {
      onMutate: async (mutatingValue: Value) => {
        client.cancelQueries(cacheKey)

        /**
         * An extra cache invalidation. Is useful to invalidate the entire
         * hierarchy of keys. Read more:
         * https://react-query.tanstack.com/guides/query-keys#array-keys
         *
         * It can't be cancelled in a single operation with `cacheKey`
         * (e.g. client.cancelQueries([cacheKey, invalidate])) because
         * `cacheKey` is an array. If we add `invalidate` to the end of it, it
         * will not work.
         *
         */
        client.cancelQueries(invalidate)

        const snapshot = client.getQueryData<API.CacheSnapshot<State>>(cacheKey)

        if (!!cacheUpdater && !!snapshot && cacheUpdateMode === 'optimistic') {
          updateCache(snapshot, mutatingValue)
        }

        /**
         * `snapshot` is returned so can be added automagically to `context`
         * property
         *
         * If a mutation returns an error, the method below will pass
         * `context.snapshot` to the current cache, so we can rollback to a
         * previous version.
         */
        return { snapshot }
      },
      onError: (error: any, value: Value, context: any) => {
        // Rollback to the previous version
        !!context?.snapshot && client.setQueryData(cacheKey, context?.snapshot)
      },
      onSettled: (data: any, error: any, variables: Value, context: any) => {
        if (!error && !!cacheUpdater && cacheUpdateMode === 'settled') {
          updateCache(context?.snapshot, data?.data)
        }

        client.invalidateQueries(cacheKey)
        /**
         * Same as `client.cancelQueries` above.
         * TL;DR: don't mix `cacheKey` and `invalidate`.
         */
        client.invalidateQueries(invalidate, { refetchInactive: true })

        // Callback (if exists)
        callback?.(data)
      }
    }
  }

  return update
}

export default useOptimisticUpdate
